import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import Button from 'views/Dashboards-B2B/components/Button';

interface BillingSettingsModalProps {
    open: boolean;
    settings: {
        auto_billing_enabled?: boolean;
        billing_email?: string;
    } | null;
    onClose: () => void;
    onSave: (settings: { auto_billing_enabled: boolean; billing_email: string }) => void;
}

const BillingSettingsModal: React.FC<BillingSettingsModalProps> = ({
    open,
    settings,
    onClose,
    onSave,
}) => {
    const [autoBilling, setAutoBilling] = useState(false);
    const [billingEmail, setBillingEmail] = useState('');

    useEffect(() => {
        if (settings) {
            setAutoBilling(settings.auto_billing_enabled ?? false);
            setBillingEmail(settings.billing_email ?? '');
        }
    }, [settings]);

    if (!open) return null;

    const handleSave = () => {
        onSave({
            auto_billing_enabled: autoBilling,
            billing_email: billingEmail,
        });
        onClose();
    };

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
                <CloseButton onClick={onClose}>✕</CloseButton>

                <ModalHeader>
                    <ModalTitle>Billing Settings</ModalTitle>
                    <ModalSubtitle>Manage your billing preferences and notifications</ModalSubtitle>
                </ModalHeader>

                <SettingsSection>
                    <SectionTitle>Payment Settings</SectionTitle>

                    <SettingRow>
                        <SettingInfo>
                            <SettingLabel>
                                <SettingIcon>💳</SettingIcon>
                                Auto-Billing
                            </SettingLabel>
                            <SettingDescription>
                                Automatically charge your default payment method when invoices are
                                generated. Prevents service interruptions due to missed payments.
                            </SettingDescription>
                        </SettingInfo>
                        <ToggleSwitch>
                            <ToggleInput
                                type="checkbox"
                                checked={autoBilling}
                                onChange={(e) => setAutoBilling(e.target.checked)}
                            />
                            <ToggleSlider />
                        </ToggleSwitch>
                    </SettingRow>
                </SettingsSection>

                <SettingsSection>
                    <SectionTitle>Email Notifications</SectionTitle>

                    <EmailField>
                        <EmailLabel>
                            <SettingIcon>📧</SettingIcon>
                            Billing Email Address
                        </EmailLabel>
                        <EmailInput
                            type="email"
                            value={billingEmail}
                            onChange={(e) => setBillingEmail(e.target.value)}
                            placeholder="billing@yourcompany.com"
                        />
                        <EmailHint>
                            We'll send invoices, receipts, and billing notifications to this email
                        </EmailHint>
                    </EmailField>
                </SettingsSection>

                <InfoBox>
                    <InfoIcon>ℹ️</InfoIcon>
                    <InfoText>
                        <strong>Note:</strong> Invoice emails and payment reminders are always
                        enabled for all customers. To update your payment method or manage your
                        subscription, use the "Manage Billing" portal.
                    </InfoText>
                </InfoBox>

                <ButtonGroup>
                    <Button
                        background="transparent"
                        border="1px solid #d1d5db"
                        color="#374151"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button background="purple" onClick={handleSave}>
                        Save Settings
                    </Button>
                </ButtonGroup>
            </ModalContent>
        </ModalOverlay>
    );
};

export default BillingSettingsModal;

// Styled Components
const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: #ffffff;
    border-radius: 12px;
    max-width: 700px;
    width: 95%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    padding: 32px;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 16px;
    right: 16px;
    background: transparent;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #6b7280;
    padding: 4px 8px;

    &:hover {
        color: #111827;
    }
`;

const ModalHeader = styled.div`
    margin-bottom: 32px;
`;

const ModalTitle = styled.h2`
    font-size: 24px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 8px 0;
`;

const ModalSubtitle = styled.p`
    font-size: 14px;
    color: #6b7280;
    margin: 0;
`;

const SettingsSection = styled.div`
    margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 16px 0;
    padding-bottom: 8px;
    border-bottom: 1px solid #e5e7eb;
`;

const SettingRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 16px 0;
    border-bottom: 1px solid #f3f4f6;

    &:last-child {
        border-bottom: none;
    }
`;

const SettingInfo = styled.div`
    flex: 1;
    margin-right: 24px;
`;

const SettingLabel = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const SettingIcon = styled.span`
    font-size: 18px;
`;

const SettingDescription = styled.p`
    font-size: 13px;
    color: #6b7280;
    margin: 0;
    line-height: 1.5;
`;

const ToggleSwitch = styled.label`
    position: relative;
    display: inline-block;
    width: 48px;
    height: 24px;
    flex-shrink: 0;
`;

const ToggleInput = styled.input`
    opacity: 0;
    width: 0;
    height: 0;

    &:checked + span {
        background-color: #6366f1;
    }

    &:checked + span:before {
        transform: translateX(24px);
    }
`;

const ToggleSlider = styled.span`
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #d1d5db;
    transition: 0.3s;
    border-radius: 24px;

    &:before {
        position: absolute;
        content: '';
        height: 18px;
        width: 18px;
        left: 3px;
        bottom: 3px;
        background-color: white;
        transition: 0.3s;
        border-radius: 50%;
    }
`;

const EmailField = styled.div`
    margin-bottom: 24px;
`;

const EmailLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 8px;
`;

const EmailInput = styled.input`
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
    color: #111827;
    transition: border-color 0.2s;

    &:focus {
        outline: none;
        border-color: #6366f1;
        box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
    }

    &::placeholder {
        color: #9ca3af;
    }
`;

const EmailHint = styled.p`
    font-size: 12px;
    color: #6b7280;
    margin: 6px 0 0 0;
`;

const InfoBox = styled.div`
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    padding: 16px;
    display: flex;
    gap: 12px;
    margin-bottom: 24px;
`;

const InfoIcon = styled.div`
    font-size: 20px;
    flex-shrink: 0;
`;

const InfoText = styled.p`
    font-size: 13px;
    color: #1e40af;
    margin: 0;
    line-height: 1.5;

    strong {
        font-weight: 600;
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
`;
